import { openOAuthSession } from './oauth-browser';
import { approveLinkOrderId, FROM_WHATSAPP, ORDERS_PATH, whatsAppChatUrl } from '@/lib/supplier-orders/approve-link';

/**
 * Approving a supplier order in the app: the pay sheet in the in-app browser
 * (SFSafariViewController / Custom Tab), where the passkey is Face ID.
 *
 * Two ways in. The order card's Approve button (`SupplierOrder.tsx`), and the
 * approve link Mucka sends on WhatsApp, which is a universal link and so opens
 * the app rather than Safari, where the owner had to sign in to Mucka first
 * (Tom, 4 Nov 2026). Before, the app navigated its WebView to that link and
 * the route would have put the pay page inside it, where a passkey cannot run.
 */

async function orderStatus(orderId: string): Promise<string | null> {
  const res = await fetch(`/api/supplier-orders/${encodeURIComponent(orderId)}`, { cache: 'no-store' }).catch(() => null);
  if (!res?.ok) return null;
  return ((await res.json().catch(() => ({}))) as { status?: string }).status ?? null;
}

/**
 * Open the pay sheet for an order, and close it by itself once the approval
 * has landed: opened plain, its Back to Mucka could not close it, and the
 * approved owner was left on a page with nothing to tap (Tom, 3 Nov 2026).
 * Resolves with the order's status once the sheet is gone.
 */
export async function openOrderApprovalSheet(payUrl: string, orderId: string, fromWhatsApp = false): Promise<string | null> {
  await openOAuthSession(`${payUrl}&app=1${fromWhatsApp ? `&${FROM_WHATSAPP}` : ''}`, {
    closeWhen: async () => {
      const s = await orderStatus(orderId);
      return !!s && s !== 'awaiting_approval';
    },
  });
  return orderStatus(orderId);
}

/** The approval has gone through: the order is on its way, not stopped. */
const ON_ITS_WAY: ReadonlySet<string> = new Set(['approved', 'running', 'needs_answer', 'ordered', 'rehearsed']);

/**
 * A WhatsApp approve link opened in the app. Opens the pay sheet straight
 * away, and once approved hands them back to WhatsApp, where they asked and
 * where the order's news arrives. Anything else (signed out, the order no
 * longer waiting, the sheet closed unapproved) lands on the Orders list, which
 * shows the order as it really is. True when the link was one of these.
 */
export function openApproveLink(url: URL): boolean {
  const orderId = approveLinkOrderId(url.pathname);
  if (!orderId) return false;
  void (async () => {
    const res = await fetch('/api/supplier-orders/pay-ticket', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ purpose: 'approve_order', orderId }),
    }).catch(() => null);
    const body = res?.ok ? ((await res.json().catch(() => ({}))) as { url?: string }) : {};
    if (!body.url) {
      window.location.assign(ORDERS_PATH);
      return;
    }
    const status = await openOrderApprovalSheet(body.url, orderId, true);
    const back = whatsAppChatUrl();
    // A top-level navigation off our origin is what Capacitor hands to the OS,
    // and wa.me is WhatsApp's universal link (`Browser.open` never opens an app).
    if (status && ON_ITS_WAY.has(status) && back) window.location.href = back;
    else window.location.assign(ORDERS_PATH);
  })();
  return true;
}
